/**
 * TikTok Condensed Settings Panel
 * Simplified version for Upload-Post integration
 * Only shows: Privacy Level + Brand Content Disclosure
 * 
 * Full TikTok compliance handled by Upload-Post API
 */

import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Globe, 
  Users, 
  Lock, 
  Tag,
  AlertCircle,
  Check
} from "lucide-react";

// Privacy options
const PRIVACY_OPTIONS = [
  { value: "PUBLIC_TO_EVERYONE", label: "Everyone", icon: Globe, description: "Anyone on TikTok can view" },
  { value: "MUTUAL_FOLLOW_FRIENDS", label: "Friends", icon: Users, description: "Only mutual followers" },
  { value: "FOLLOWER_OF_CREATOR", label: "Followers", icon: Users, description: "Only your followers" },
  { value: "SELF_ONLY", label: "Only Me", icon: Lock, description: "Private - only you can view" },
];

export default function TikTokCondensedSettings({ 
  settings, 
  onSettingsChange, 
  disabled = false 
}) {
  const [privacyLevel, setPrivacyLevel] = useState(settings?.privacy_level || "PUBLIC_TO_EVERYONE");
  const [brandContentEnabled, setBrandContentEnabled] = useState(settings?.brand_content_toggle || false);
  
  // Update parent when settings change
  useEffect(() => {
    onSettingsChange({
      privacy_level: privacyLevel,
      brand_content_toggle: brandContentEnabled,
      // Defaults for Upload-Post
      allow_comment: true,
      allow_duet: !brandContentEnabled, // Disabled for branded content
      allow_stitch: !brandContentEnabled,
    });
  }, [privacyLevel, brandContentEnabled]);
  
  // Branded content requires non-private visibility
  const isPrivate = privacyLevel === "SELF_ONLY";
  
  return (
    <div className="space-y-4">
      {/* Privacy Level */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-viraly-light flex items-center gap-2">
          <Globe className="w-4 h-4 text-viraly-muted" />
          Who can view this video?
        </label>
        <div className="grid grid-cols-2 gap-2">
          {PRIVACY_OPTIONS.map((option) => {
            const Icon = option.icon;
            const isSelected = privacyLevel === option.value;
            
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => !disabled && setPrivacyLevel(option.value)}
                disabled={disabled}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  isSelected
                    ? "border-viraly-orange bg-viraly-orange/10"
                    : "border-white/10 bg-white/5 hover:border-white/20"
                } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`tiktok-privacy-${option.value}`}
              >
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${isSelected ? "text-viraly-orange" : "text-viraly-muted"}`} />
                  <span className={`text-sm font-medium ${isSelected ? "text-viraly-orange" : "text-viraly-light"}`}>
                    {option.label}
                  </span>
                  {isSelected && <Check className="w-3 h-3 text-viraly-orange ml-auto" />}
                </div>
                <p className="text-[10px] text-viraly-muted mt-1">{option.description}</p>
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Brand Content Disclosure Toggle */}
      <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3">
        <div className="flex items-start gap-3">
          <Tag className="w-5 h-5 text-blue-400 mt-0.5" />
          <div className="flex-1">
            <label className="text-sm font-medium text-viraly-light">
              Brand Content Disclosure
            </label>
            <p className="text-xs text-viraly-muted mt-1">
              Turn on if this post promotes a brand, product, or paid partnership
            </p>
          </div>
          <button
            type="button"
            onClick={() => !disabled && !isPrivate && setBrandContentEnabled(!brandContentEnabled)}
            disabled={disabled || isPrivate}
            className={`relative w-12 h-6 rounded-full transition-all ${
              brandContentEnabled ? "bg-blue-500" : "bg-white/20"
            } ${(disabled || isPrivate) ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="tiktok-brand-content-toggle"
          >
            <motion.div
              animate={{ x: brandContentEnabled ? 24 : 2 }}
              className="absolute top-1 w-4 h-4 rounded-full bg-white shadow-md"
            />
          </button>
        </div>
        
        {isPrivate && (
          <div className="flex items-center gap-2 p-2 rounded bg-yellow-500/10 text-yellow-400">
            <AlertCircle className="w-4 h-4" />
            <span className="text-xs">Branded content requires public or friends visibility</span>
          </div>
        )}
        
        {brandContentEnabled && !isPrivate && (
          <div className="text-xs text-blue-400 flex items-center gap-1">
            <Check className="w-3 h-3" />
            TikTok will add "Paid Partnership" label to your video
          </div>
        )}
      </div>
      
      {/* TikTok Compliance Note */}
      <p className="text-[10px] text-viraly-muted/60 text-center">
        By posting, you agree to TikTok's Community Guidelines and Terms of Service
      </p>
    </div>
  );
}
